/**
 * EmotionalMixer - UI for visualizing and adjusting track emotional analysis
 *
 * Shows:
 * - Mood arc timeline visualization
 * - Energy curve display
 * - Color palette from visual recommendations
 * - Mood adjustment sliders
 * - Trim segment selection (15s/30s/60s)
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { Palette, Zap, Music, Scissors, ThermometerSun, Activity, Play, Pause, Square } from 'lucide-react';
import type { TrackContext } from '../../types';

interface TrimRecommendation {
  duration: number;
  start_time: number;
  end_time: number;
  score: number;
  rationale: string;
  includes_peak: boolean;
  hook_energy: number;
}

interface TrimSelection {
  start_time: number;
  end_time: number;
  duration: number;
}

interface EmotionalAdjustments {
  mood_bias: Record<string, number>;
  color_temperature: number;
  motion_intensity: number;
}

interface WordTimestamp {
  word: string;
  start: number;
  end: number;
}

interface EmotionalMixerProps {
  track: TrackContext;
  trimRecommendations?: TrimRecommendation[];
  onTrimSelect: (trim: TrimSelection) => void;
  onMoodAdjust: (adjustments: EmotionalAdjustments) => void;
  selectedTrim?: TrimSelection;
  adjustments?: EmotionalAdjustments;
  audioUrl?: string;
  wordTimestamps?: WordTimestamp[];
}

// Mood colors for visualization
const MOOD_COLORS: Record<string, string> = {
  energetic: '#f59e0b',
  euphoric: '#ec4899',
  melancholic: '#6366f1',
  peaceful: '#10b981',
  aggressive: '#ef4444',
  mysterious: '#8b5cf6',
  tense: '#f97316',
  hopeful: '#14b8a6',
};

export default function EmotionalMixer({
  track,
  trimRecommendations = [],
  onTrimSelect,
  onMoodAdjust,
  selectedTrim,
  adjustments = { mood_bias: {}, color_temperature: 0, motion_intensity: 0.5 },
  audioUrl,
  wordTimestamps = [],
}: EmotionalMixerProps) {
  const [localAdjustments, setLocalAdjustments] = useState<EmotionalAdjustments>(adjustments);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Audio playback effect - sync with selected trim
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setPlaybackTime(audio.currentTime);
      // Stop at end of trim selection
      if (selectedTrim && audio.currentTime >= selectedTrim.end_time) {
        audio.pause();
        audio.currentTime = selectedTrim.start_time;
        setIsPlaying(false);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (selectedTrim) {
        audio.currentTime = selectedTrim.start_time;
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [selectedTrim]);

  const toggleAudioPlayback = () => {
    const audio = audioRef.current;
    if (!audio || !selectedTrim) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // Target the exact trim start time
      // Note: If audio is consistently offset, the issue is in the trim recommendation timestamps,
      // not in the playback. The trim's start_time should match when those lyrics actually occur.
      const targetTime = selectedTrim.start_time;

      console.log('[EmotionalMixer] Playing trim:', {
        targetTime,
        trimStart: selectedTrim.start_time,
        trimEnd: selectedTrim.end_time,
        duration: selectedTrim.duration,
        currentAudioTime: audio.currentTime,
        audioDuration: audio.duration,
      });

      // Always seek to trim start when starting playback
      if (audio.currentTime < selectedTrim.start_time - 0.5 || audio.currentTime >= selectedTrim.end_time) {
        audio.currentTime = targetTime;

        // Wait for the seek to complete before playing
        const handleSeeked = () => {
          audio.removeEventListener('seeked', handleSeeked);
          console.log('[EmotionalMixer] Seek completed, actual position:', audio.currentTime, 'target was:', targetTime);
          audio.play().catch((e) => console.error('[EmotionalMixer] Play failed:', e));
          setIsPlaying(true);
        };
        audio.addEventListener('seeked', handleSeeked);
      } else {
        // Already at correct position, play immediately
        console.log('[EmotionalMixer] Already in range, playing from:', audio.currentTime);
        audio.play().catch((e) => console.error('[EmotionalMixer] Play failed:', e));
        setIsPlaying(true);
      }
    }
  };

  const stopAudioPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    if (selectedTrim) {
      audio.currentTime = selectedTrim.start_time;
    }
    setIsPlaying(false);
  };

  // Extract data from track context - using type assertions for nested objects
  // Note: Data structure from FastAnalyzer:
  // - context_data.timeline.energy_curve, mood_indicators
  // - context_data.ai_analysis.visual_recommendations, musical_intelligence
  // - context_data.technical (tempo_bpm, duration_seconds, etc.)
  // - context_data.transcription.text (lyrics)
  const contextData = (track.context_data || {}) as Record<string, unknown>;
  const timeline = (contextData.timeline || {}) as Record<string, unknown>;
  const aiAnalysis = (contextData.ai_analysis || {}) as Record<string, unknown>;

  // Energy curve from timeline
  const energyCurve = (timeline.energy_curve || []) as Array<{ time: number; energy: number }>;

  // Mood timeline - convert mood_indicators to timeline format if needed
  const moodIndicators = (timeline.mood_indicators || {}) as Record<string, number>;
  const moodTimeline = useMemo(() => {
    // If we have mood_timeline directly, use it
    if (Array.isArray(timeline.mood_timeline)) {
      return timeline.mood_timeline as Array<{ start: number; end: number; mood: string; intensity: number }>;
    }
    // Convert mood_indicators to a simple timeline representation
    const totalDur = track.duration_seconds || 180;
    const moods = Object.entries(moodIndicators);
    if (moods.length === 0) return [];

    // Create segments based on mood indicators
    const segmentDuration = totalDur / Math.max(moods.length, 1);
    return moods.map(([mood, intensity], i) => ({
      start: i * segmentDuration,
      end: (i + 1) * segmentDuration,
      mood,
      intensity: typeof intensity === 'number' ? intensity : 0.5,
    }));
  }, [timeline, moodIndicators, track.duration_seconds]);

  // Visual and musical intelligence from ai_analysis
  const visualRecs = (aiAnalysis.visual_recommendations || {}) as Record<string, unknown>;
  const musicalIntel = (aiAnalysis.musical_intelligence || {}) as Record<string, unknown>;
  const emotionalArc = (musicalIntel.emotional_arc || {}) as Record<string, unknown>;
  const moodProfile = (musicalIntel.mood_profile || {}) as Record<string, unknown>;
  const technical = (contextData.technical || {}) as Record<string, unknown>;

  const totalDuration = (technical.duration_seconds as number) || track.duration_seconds || 180;

  // Get primary mood and color palette
  const primaryMood = (moodProfile.primary_mood as string) || track.primary_mood || 'energetic';
  const colorPalette = (visualRecs.color_palette || {}) as Record<string, string>;

  // Calculate energy at specific points for display
  const avgEnergy = useMemo(() => {
    if (!energyCurve.length) return 0.5;
    const sum = energyCurve.reduce((acc: number, e: { energy: number }) => acc + e.energy, 0);
    return sum / energyCurve.length;
  }, [energyCurve]);

  const handleAdjustmentChange = (key: keyof EmotionalAdjustments, value: number | Record<string, number>) => {
    const newAdjustments = { ...localAdjustments, [key]: value };
    setLocalAdjustments(newAdjustments);
    onMoodAdjust(newAdjustments);
  };

  const handleMoodBiasChange = (mood: string, value: number) => {
    const newBias = { ...localAdjustments.mood_bias, [mood]: value };
    handleAdjustmentChange('mood_bias', newBias);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get lyrics at a specific time range from word timestamps
  const getLyricsInRange = (startTime: number, endTime: number, maxWords: number = 15): string => {
    if (!wordTimestamps.length) return '';
    const words = wordTimestamps
      .filter((w) => w.start >= startTime && w.start < endTime)
      .slice(0, maxWords)
      .map((w) => w.word);
    return words.join(' ') + (words.length >= maxWords ? '...' : '');
  };

  // Get the expected first words at trim start (for verification)
  const expectedLyricsAtTrimStart = useMemo(() => {
    if (!selectedTrim || !wordTimestamps.length) return null;
    const lyrics = getLyricsInRange(selectedTrim.start_time, selectedTrim.start_time + 5);
    return lyrics || null;
  }, [selectedTrim, wordTimestamps]);

  return (
    <div className="space-y-6">
      {/* Mood Arc Visualization */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-jelly-400" />
          <h3 className="font-medium">Mood Arc</h3>
          <span className="text-xs text-zinc-500 ml-auto">{formatTime(totalDuration)} total</span>
        </div>

        {/* Timeline visualization */}
        <div className="relative h-12 bg-zinc-900 rounded-lg overflow-hidden">
          {moodTimeline.length > 0 ? (
            moodTimeline.map((segment: { start: number; end: number; mood: string; intensity: number }, i: number) => {
              const left = (segment.start / totalDuration) * 100;
              const width = ((segment.end - segment.start) / totalDuration) * 100;
              const color = MOOD_COLORS[segment.mood] || '#6366f1';

              return (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 transition-opacity hover:opacity-80"
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    backgroundColor: color,
                    opacity: 0.3 + segment.intensity * 0.7,
                  }}
                  title={`${segment.mood} (${Math.round(segment.intensity * 100)}%)`}
                />
              );
            })
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm">
              No mood timeline available
            </div>
          )}

          {/* Selected trim overlay */}
          {selectedTrim && (
            <div
              className="absolute top-0 bottom-0 border-2 border-jelly-500 bg-jelly-500/20"
              style={{
                left: `${(selectedTrim.start_time / totalDuration) * 100}%`,
                width: `${((selectedTrim.end_time - selectedTrim.start_time) / totalDuration) * 100}%`,
              }}
            />
          )}

          {/* Peak moment marker */}
          {(() => {
            const peakMoment = emotionalArc.peak_moment as { time?: number; description?: string } | undefined;
            return peakMoment?.time ? (
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-yellow-400"
                style={{ left: `${(peakMoment.time / totalDuration) * 100}%` }}
                title={`Peak: ${peakMoment.description || ''}`}
              />
            ) : null;
          })()}

          {/* Playback position indicator */}
          {isPlaying && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white z-20 transition-all"
              style={{ left: `${(playbackTime / totalDuration) * 100}%` }}
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rounded-full" />
            </div>
          )}
        </div>

        {/* Mood legend */}
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(MOOD_COLORS).slice(0, 6).map(([mood, color]) => (
            <div key={mood} className="flex items-center gap-1 text-xs">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-zinc-400 capitalize">{mood}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Energy Curve */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-yellow-400" />
          <h3 className="font-medium">Energy</h3>
          <span className="text-xs text-zinc-500 ml-auto">Avg: {Math.round(avgEnergy * 100)}%</span>
        </div>

        {/* Simple energy bar representation */}
        <div className="h-8 bg-zinc-900 rounded-lg overflow-hidden flex">
          {energyCurve.length > 0 ? (
            energyCurve.slice(0, 20).map((point: { time: number; energy: number }, i: number) => (
              <div
                key={i}
                className="flex-1 flex items-end"
                title={`${formatTime(point.time)}: ${Math.round(point.energy * 100)}%`}
              >
                <div
                  className="w-full bg-gradient-to-t from-yellow-600 to-yellow-400 transition-all"
                  style={{ height: `${point.energy * 100}%` }}
                />
              </div>
            ))
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
              No energy data
            </div>
          )}
        </div>
      </div>

      {/* Trim Selection */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Scissors className="w-4 h-4 text-purple-400" />
            <h3 className="font-medium">Trim for TikTok</h3>
          </div>
          {/* Audio Preview Controls */}
          {audioUrl && selectedTrim && (
            <div className="flex items-center gap-2">
              <button
                onClick={toggleAudioPlayback}
                className={`p-2 rounded-lg transition-colors ${
                  isPlaying ? 'bg-jelly-500 text-white' : 'bg-zinc-700 hover:bg-zinc-600'
                }`}
                title={isPlaying ? 'Pause preview' : 'Preview trim'}
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              {isPlaying && (
                <button
                  onClick={stopAudioPlayback}
                  className="p-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
                  title="Stop"
                >
                  <Square className="w-4 h-4" />
                </button>
              )}
              {isPlaying && (
                <span className="text-xs text-zinc-400 font-mono">
                  {formatTime(playbackTime - selectedTrim.start_time)} / {formatTime(selectedTrim.duration)}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {[15, 30, 60].map((duration) => {
            const rec = trimRecommendations.find((r) => r.duration === duration);
            const isSelected = selectedTrim?.duration === duration;

            return (
              <button
                key={duration}
                onClick={() => {
                  if (rec) {
                    onTrimSelect({
                      start_time: rec.start_time,
                      end_time: rec.end_time,
                      duration: rec.duration,
                    });
                  } else {
                    // Default trim from start
                    onTrimSelect({
                      start_time: 0,
                      end_time: Math.min(duration, totalDuration),
                      duration,
                    });
                  }
                }}
                className={`p-3 rounded-lg border transition-all ${
                  isSelected
                    ? 'border-jelly-500 bg-jelly-500/20'
                    : 'border-zinc-700 hover:border-zinc-600'
                }`}
              >
                <div className="text-lg font-bold">{duration}s</div>
                {rec && (
                  <>
                    <div className="text-xs text-zinc-400 mt-1">
                      {formatTime(rec.start_time)} - {formatTime(rec.end_time)}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <div
                        className="h-1 rounded-full bg-gradient-to-r from-jelly-500 to-purple-500"
                        style={{ width: `${rec.score * 100}%` }}
                      />
                      <span className="text-xs text-zinc-500">{Math.round(rec.score * 100)}%</span>
                    </div>
                    {rec.includes_peak && (
                      <div className="text-xs text-yellow-400 mt-1">Includes peak</div>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>

        {selectedTrim && trimRecommendations.find((r) => r.duration === selectedTrim.duration)?.rationale && (
          <p className="text-xs text-zinc-400 mt-3 italic">
            {trimRecommendations.find((r) => r.duration === selectedTrim.duration)?.rationale}
          </p>
        )}

        {/* Show expected lyrics at trim start for verification */}
        {selectedTrim && expectedLyricsAtTrimStart && (
          <div className="mt-3 p-2 bg-zinc-900/50 rounded-lg">
            <p className="text-xs text-zinc-500 mb-1">Expected lyrics at {formatTime(selectedTrim.start_time)}:</p>
            <p className="text-xs text-zinc-300 font-mono">&quot;{expectedLyricsAtTrimStart}&quot;</p>
          </div>
        )}
      </div>

      {/* Color Palette */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Palette className="w-4 h-4 text-pink-400" />
          <h3 className="font-medium">Color Palette</h3>
        </div>

        <div className="flex gap-2">
          {colorPalette.primary && (
            <div className="flex-1">
              <div
                className="h-12 rounded-lg"
                style={{ backgroundColor: colorPalette.primary }}
              />
              <p className="text-xs text-zinc-400 mt-1 text-center">Primary</p>
            </div>
          )}
          {colorPalette.secondary && (
            <div className="flex-1">
              <div
                className="h-12 rounded-lg"
                style={{ backgroundColor: colorPalette.secondary }}
              />
              <p className="text-xs text-zinc-400 mt-1 text-center">Secondary</p>
            </div>
          )}
          {colorPalette.accent && (
            <div className="flex-1">
              <div
                className="h-12 rounded-lg"
                style={{ backgroundColor: colorPalette.accent }}
              />
              <p className="text-xs text-zinc-400 mt-1 text-center">Accent</p>
            </div>
          )}
          {!colorPalette.primary && !colorPalette.secondary && !colorPalette.accent && (
            <div className="flex-1 h-12 rounded-lg bg-zinc-700 flex items-center justify-center text-zinc-500 text-sm">
              No palette available
            </div>
          )}
        </div>

        {/* Color temperature slider */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
            <span>Cool</span>
            <ThermometerSun className="w-3 h-3" />
            <span>Warm</span>
          </div>
          <input
            type="range"
            min={-1}
            max={1}
            step={0.1}
            value={localAdjustments.color_temperature}
            onChange={(e) => handleAdjustmentChange('color_temperature', parseFloat(e.target.value))}
            className="w-full accent-jelly-500"
          />
        </div>
      </div>

      {/* Mood Adjustments */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Music className="w-4 h-4 text-blue-400" />
          <h3 className="font-medium">Mood Adjustments</h3>
        </div>

        <div className="space-y-3">
          {/* Energy vs Calm */}
          <div>
            <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
              <span>Calm</span>
              <span>Energetic</span>
            </div>
            <input
              type="range"
              min={-1}
              max={1}
              step={0.1}
              value={localAdjustments.mood_bias.energetic || 0}
              onChange={(e) => handleMoodBiasChange('energetic', parseFloat(e.target.value))}
              className="w-full accent-yellow-500"
            />
          </div>

          {/* Happy vs Melancholic */}
          <div>
            <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
              <span>Melancholic</span>
              <span>Euphoric</span>
            </div>
            <input
              type="range"
              min={-1}
              max={1}
              step={0.1}
              value={localAdjustments.mood_bias.euphoric || 0}
              onChange={(e) => handleMoodBiasChange('euphoric', parseFloat(e.target.value))}
              className="w-full accent-pink-500"
            />
          </div>

          {/* Motion Intensity */}
          <div>
            <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
              <span>Subtle</span>
              <span>Motion Intensity</span>
              <span>Dynamic</span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={localAdjustments.motion_intensity}
              onChange={(e) => handleAdjustmentChange('motion_intensity', parseFloat(e.target.value))}
              className="w-full accent-purple-500"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <div className="text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-zinc-400">Primary Mood</span>
            <span className="capitalize">{primaryMood}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span className="text-zinc-400">Selected Duration</span>
            <span>{selectedTrim ? `${selectedTrim.duration}s` : 'None'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-zinc-400">BPM</span>
            <span>{track.tempo_bpm ? Math.round(track.tempo_bpm) : 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* Hidden audio element for preview */}
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="auto" />}
    </div>
  );
}
